import jsPDF from "jspdf";

const KIND_TITLES = {
  forelaggande: "Beslut om föreläggande",
  forelaggande_vite: "Beslut om föreläggande förenat med vite",
  byggsanktionsavgift: "Beslut om byggsanktionsavgift",
  forbud_arbete: "Beslut om förbud mot fortsatt arbete",
  forbud_anvandning: "Beslut om förbud mot användning",
  avskrivning: "Avskrivningsbeslut",
  rattelse: "Slutbeslut – rättelse utförd",
  generic: "Beslut i tillsynsärende",
};

const SOURCE_LABELS = {
  klagomal: "Klagomål",
  anmalan: "Anmälan",
  egen_upptackt: "Egen upptäckt",
};

export function inferDocumentKind(tillsyn) {
  const tags = tillsyn.tags || [];
  if (tags.includes("vite")) return "forelaggande_vite";
  if (tags.includes("byggsanktionsavgift")) return "byggsanktionsavgift";
  if (tags.includes("forbud_arbete")) return "forbud_arbete";
  if (tags.includes("forbud_anvandning")) return "forbud_anvandning";
  if (tags.some((t) => t.startsWith("foreleggande"))) return "forelaggande";
  if (tillsyn.outcome?.startsWith("avskrivet")) return "avskrivning";
  if (tillsyn.outcome === "avslutat_rattelse") return "rattelse";
  return "generic";
}

function fmtDate(iso) {
  if (!iso) return new Date().toLocaleDateString("sv-SE");
  return new Date(iso).toLocaleDateString("sv-SE");
}

export function exportTillsynPdf(tillsyn, opts = {}) {
  const {
    kind = inferDocumentKind(tillsyn),
    vite_amount = "",
    frist_date = "",
    handlaggare_name = "",
    diary_number = tillsyn.ref,
    municipality = "Stockholms stad",
    notes = "",
  } = opts;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, H = 297, M = 20;
  let y = M;

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text(municipality.toUpperCase(), M, y);
  doc.text("BYGGNADSNÄMNDEN", W - M, y, { align: "right" });
  y += 4;
  doc.setDrawColor(180);
  doc.line(M, y, W - M, y);
  y += 10;

  // Title block
  doc.setFontSize(11);
  doc.setTextColor(60);
  doc.text(KIND_TITLES[kind] || KIND_TITLES.generic, M, y);
  y += 8;
  doc.setFontSize(20);
  doc.setTextColor(20);
  doc.text(tillsyn.title || "Tillsynsärende", M, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(`Diarienummer: ${diary_number}    ·    Beslutsdatum: ${fmtDate()}`, M, y);
  y += 10;

  // Property block
  const propRows = [
    ["Adress", tillsyn.address || "-"],
    ["Fastighet", tillsyn.fastighet || "-"],
    ["Kategori", tillsyn.category || "-"],
    ["Källa", SOURCE_LABELS[tillsyn.source] || tillsyn.source || "-"],
  ];
  doc.setDrawColor(220);
  doc.setFillColor(248, 248, 250);
  doc.rect(M, y, W - 2 * M, propRows.length * 6 + 4, "F");
  doc.setTextColor(40);
  doc.setFontSize(9);
  propRows.forEach(([k, v], i) => {
    doc.setFont("helvetica", "bold");
    doc.text(k.toUpperCase(), M + 3, y + 6 + i * 6);
    doc.setFont("helvetica", "normal");
    doc.text(String(v), M + 38, y + 6 + i * 6);
  });
  y += propRows.length * 6 + 10;

  // Description
  if (tillsyn.description) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("ÄRENDETS BAKGRUND", M, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const desc = doc.splitTextToSize(tillsyn.description, W - 2 * M);
    doc.text(desc, M, y);
    y += desc.length * 4.5 + 6;
  }

  // Outcome / Beslut
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("BESLUT", M, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const beslutText = tillsyn.outcome_label
    || `Pågående utredning – status: ${tillsyn.status}`;
  const beslutLines = doc.splitTextToSize(beslutText, W - 2 * M);
  doc.text(beslutLines, M, y);
  y += beslutLines.length * 5 + 4;

  if (kind === "forelaggande_vite" && vite_amount) {
    doc.setFont("helvetica", "bold");
    doc.text(`Vite: ${vite_amount} kr`, M, y);
    y += 6;
    doc.setFont("helvetica", "normal");
  }
  if (frist_date) {
    doc.setFont("helvetica", "bold");
    doc.text(`Frist för rättelse: ${frist_date}`, M, y);
    y += 6;
    doc.setFont("helvetica", "normal");
  }
  y += 4;

  // Decisions audit-trail
  if (tillsyn.decisions?.length) {
    if (y > H - 60) { doc.addPage(); y = M; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("HANDLÄGGNINGSLOGG", M, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    tillsyn.decisions.forEach((d) => {
      if (y > H - 25) { doc.addPage(); y = M; }
      const line = `Steg ${d.step} (${fmtDate(d.at)}) – ${d.label}${d.tag ? `  [${d.tag}]` : ""}`;
      doc.text(line, M, y);
      y += 4.5;
      if (d.note) {
        const noteLines = doc.splitTextToSize(`  "${d.note}"`, W - 2 * M - 5);
        doc.setTextColor(110);
        doc.text(noteLines, M + 3, y);
        doc.setTextColor(40);
        y += noteLines.length * 4 + 1;
      }
      doc.setTextColor(140);
      doc.text(`  av ${d.by}`, M + 3, y);
      doc.setTextColor(40);
      y += 5;
    });
    y += 4;
  }

  // Tags
  if (tillsyn.tags?.length) {
    if (y > H - 35) { doc.addPage(); y = M; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("VIDTAGNA ÅTGÄRDER", M, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(tillsyn.tags.join(", "), M, y);
    y += 8;
  }

  // Notes
  if (notes) {
    if (y > H - 30) { doc.addPage(); y = M; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("MOTIVERING", M, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(notes, W - 2 * M);
    doc.text(lines, M, y);
    y += lines.length * 4.5 + 4;
  }

  // Legal footer + signature
  if (y > H - 50) { doc.addPage(); y = M; }
  y = Math.max(y, H - 55);
  doc.setDrawColor(220);
  doc.line(M, y, W - M, y);
  y += 6;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(110);
  const legal = "Detta beslut är fattat av byggnadsnämnden med stöd av plan- och bygglagen (PBL 11 kap.). Beslutet kan överklagas till länsstyrelsen inom tre (3) veckor från delfåendet. Överklagandet ska ges in till byggnadsnämnden.";
  doc.text(doc.splitTextToSize(legal, W - 2 * M), M, y);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(40);
  doc.text("För byggnadsnämnden", M, y);
  y += 10;
  doc.line(M, y, M + 70, y);
  y += 4;
  doc.text(handlaggare_name || "Handläggare", M, y);
  doc.text(`Datum: ${fmtDate()}`, W - M - 40, y);

  const filename = `${diary_number}_${kind}.pdf`;
  doc.save(filename);
}
